import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Group,
  Text,
  Tooltip,
} from "@mantine/core";
import { useSessionStorage } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAntennaBars2,
  IconAntennaBars3,
  IconAntennaBars4,
  IconAntennaBars5,
  IconArrowsDiagonal,
  IconArrowsDiagonalMinimize2,
  IconGripHorizontal,
  IconHourglassEmpty,
  IconMicrophone,
  IconMicrophoneOff,
  IconPhone,
  IconVideo,
  IconVideoOff,
  IconX,
} from "@tabler/icons-react";
import {
  LocalUser,
  RemoteUser,
  useClientEvent,
  useIsConnected,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  useNetworkQuality,
  usePublish,
  useRemoteAudioTracks,
  useRemoteUsers,
  useRTCClient,
} from "agora-rtc-react";
import i18n from "src/i18n";

import { getInitials } from "@utils/getInitials";
import { showInfoNotification } from "@utils/showNotification";
import { truncateWithEllipsis } from "@utils/truncateText";

import { SESSION_STORAGE_KEYS } from "@constants/index";

import { useEndTeleconsultation } from "@hooks/mutations/teleconsultation/useEndTeleconsultation";
import { useStartTeleconsultation } from "@hooks/mutations/teleconsultation/useStartTeleconsultation";
import { useGetMeetingToken } from "@hooks/queries/useGetMeetingToken";
import {
  useDetectLocalVoiceActivity,
  useDetectRemoteVoiceActivity,
} from "@hooks/useDetectVoiceActivity";
import { useRTMPresence } from "@hooks/useRTMPresence";

import { useAuth } from "@contexts/auth";

import UserJoinCallSound from "@assets/sound/user-join-call.wav";

import { openBlockNavigationModal } from "../BlockNavigationModal";
import { openCallTokenExpirationModal } from "../CallTokenExpirationModal";
import { openLeaveCallWarningModal } from "../LeaveCallWarningModal";

import { StyledVideoCall, StyledVideoCallControls } from "./VideoCall.styled";


interface VideoCallProps {
  isCalling?: boolean;
  setIsCalling: (isCalling: boolean) => void;
  setCallDuration: (callDuration: number) => void;
  patientId: string;
  cId: string;
  appointmentId?: string;
  consultationId: string;
}

const getNetworkDelay = (quality: number) => {
  if (quality <= 150) {
    return <IconAntennaBars5 size={20} color="var(--mantine-color-green-4)" />;
  } else if (quality > 150 && quality <= 250) {
    return <IconAntennaBars4 size={20} color="var(--mantine-color-yellow-4)" />;
  } else if (quality > 251 && quality <= 400) {
    return <IconAntennaBars3 size={20} color="var(--mantine-color-orange-4)" />;
  } else {
    // 300+
    return <IconAntennaBars2 size={20} color="var(--mantine-color-red-4)" />;
  }
};

export const VideoCall = ({
  isCalling,
  setIsCalling,
  setCallDuration,
  patientId,
  cId,
  appointmentId,
  consultationId,
}: VideoCallProps) => {
  const { t } = useTranslation();
  const { username } = useAuth();
  const userJoinCallAudioRef = useRef<HTMLAudioElement>(null);
  const { mutate: startTeleconsultation } = useStartTeleconsultation();
  const { mutate: endTeleconsultation } = useEndTeleconsultation();
  const [_, setVideoCallThreshold] = useSessionStorage({
    key: SESSION_STORAGE_KEYS.VC_THRESHOLD,
    defaultValue: {
      [SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED]: false,
      [SESSION_STORAGE_KEYS.VC_PATIENT_JOINED]: false,
      [SESSION_STORAGE_KEYS.VC_CALL_DURATION_WITH_PATIENT]: 0,
    },
  }); // todo: Use useVideoCallThreshold hook instead of directly accessing the session storage value

  // Call stuff starts here
  const client = useRTCClient();
  const isConnected = useIsConnected();
  const [countSeconds, setCountSeconds] = useState(0);
  const { delay } = useNetworkQuality();
  // console.log(delay);
  const [startCall, setStartCall] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isTokenExpiringSoon, setIsTokenExpiringSoon] = useState(false);

  const { mutate: getMeetingToken, data: meetingToken } = useGetMeetingToken();

  const joinRes = useJoin(
    {
      appid: import.meta.env.VITE_AGORA_APP_ID,
      channel: patientId,
      token: meetingToken?.data?.token,
    },
    startCall
  );
  // console.log(meetingToken);

  useRTMPresence({
    consultationId,
    enabled: joinRes.isConnected,
  });

  // Local user
  const [isMicOn, setIsMicOn] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(isMicOn);
  const { localCameraTrack } = useLocalCameraTrack(isCameraOn);
  usePublish([localMicrophoneTrack, localCameraTrack]);
  const [isLocalVoiceDetected, setIsLocalVoiceDetected] = useState(false);
  useDetectLocalVoiceActivity({
    localMicrophoneTrack,
    isLocalVoiceDetected,
    setIsLocalVoiceDetected,
  });

  //Events
  useClientEvent(client, "user-joined", async () => {
    // console.log(user.uid);
    if (userJoinCallAudioRef?.current) {
      userJoinCallAudioRef.current.volume = 0.5;
      userJoinCallAudioRef.current.play();
    }
    showInfoNotification({
      message: i18n.t("user-joined-call", {
        patientName: meetingToken?.data?.patient_name,
      }),
    });
    setVideoCallThreshold(prev => ({
      ...prev,
      [SESSION_STORAGE_KEYS.VC_PATIENT_JOINED]: true,
    }));
  });
  useClientEvent(client, "user-left", async () => {
    showInfoNotification({
      message: i18n.t("user-left-call", {
        patientName: meetingToken?.data?.patient_name,
      }),
    });
    // console.log("Reason for leaving:", reason);
    setVideoCallThreshold(prev => ({
      ...prev,
      [SESSION_STORAGE_KEYS.VC_PATIENT_JOINED]: false,
    }));
  });

  // Remote users
  const remoteUsers = useRemoteUsers();
  // console.log("remote users:", remoteUsers);
  const { audioTracks } = useRemoteAudioTracks(remoteUsers);
  const [isRemoteVoiceDetected, setIsRemoteVoiceDetected] = useState(false);
  useDetectRemoteVoiceActivity({
    remoteAudioTracks: audioTracks,
    isRemoteVoiceDetected,
    setIsRemoteVoiceDetected,
  });

  // More Events
  useClientEvent(client, "token-privilege-will-expire", async () => {
    if (!isTokenExpiringSoon) {
      openCallTokenExpirationModal({ setIsCalling });
      setIsTokenExpiringSoon(true);
    }
  });
  useClientEvent(client, "token-privilege-did-expire", () => {
    showInfoNotification({
      title: i18n.t("call-ended"),
      message: i18n.t("call-time-limit-reached"),
      autoClose: 1000 * 10,
    });
    setIsTokenExpiringSoon(false);
    setIsCameraOn(false);
    setIsMicOn(false);
    localCameraTrack?.close();
    localMicrophoneTrack?.close();
    setTimeout(() => {
      modals.closeAll();
      setIsCalling(false);
    }, 1000);
    setVideoCallThreshold(prev => ({
      ...prev,
      [SESSION_STORAGE_KEYS.VC_PATIENT_JOINED]: false,
      [SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED]: false,
    }));
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;

    // console.log(joinRes.data); // Displays uid
    if (joinRes.isConnected && userJoinCallAudioRef?.current) {
      interval = setInterval(() => {
        setCountSeconds(countSeconds => countSeconds + 1);
      }, 1000);
      userJoinCallAudioRef.current.volume = 0.5;
      userJoinCallAudioRef.current.play();

      // updateAppointment({
      //   appointmentId: appointmentId || "",
      //   teleconsultationCallDetails: { callStatus: "started", callTime: 0 },
      // });
      startTeleconsultation(consultationId);

      setVideoCallThreshold(prev => ({
        ...prev,
        [SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED]: true,
      }));
    }
    return () => {
      clearInterval(interval);
    };
  }, [joinRes.isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    if (isMicOn) {
      showInfoNotification({ message: i18n.t("microphone-on") });
    } else {
      showInfoNotification({ message: i18n.t("microphone-off") });
    }
  }, [isMicOn]);

  useEffect(() => {
    if (joinRes.error) {
      console.error(joinRes.error);
      notifications.show({
        title: t("oops"),
        message: t("call-error"),
        autoClose: false,
        color: "red",
        icon: <IconX size={18} />,
      });
      // updateAppointment({
      //   appointmentId: appointmentId || "",
      //   teleconsultationCallDetails: {
      //     callStatus: "not_started",
      //     callTime: -1,
      //   },
      // });
    }
  }, [joinRes.error]);

  useEffect(() => {
    if (isCalling) {
      getMeetingToken(
        { patientId },
        {
          onSuccess: () => {
            setStartCall(true);
          },
        }
      );
    } else {
      setStartCall(false);
      setCountSeconds(0);
    }
  }, [isCalling]);

  // Block navigating elsewhere when in call
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      joinRes.isConnected && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      openBlockNavigationModal({
        setIsCalling,
        blocker,
        onConfirmCallback: () => endTeleconsultation(consultationId),
      });
    }

    return () => {
      if (blocker.reset) {
        blocker.reset();
      }
    };
  }, [blocker.state]);

  if (!isConnected) return;

  if (isMaximized) {
    return createPortal(
      <StyledVideoCall className={"is-maximized"}>
        <div className="navbar">
          <div className="left-section">
            <Text fz={"sm"} fw={500}>
              {new Date().toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          </div>
          <Group gap={"xs"} py={"md"} className="middle-section">
            <Text fz={"md"} fw={500}>
              {t("teleconsultation")}
            </Text>
            <Badge
              c="accent.4"
              bg="accent.0"
              ff={"var(--font-secondary)"}
              fw={450}>
              {cId}
            </Badge>
          </Group>
          <div className="right-section">
            <Group gap={"0.5rem"}>
              <Box
                style={{
                  borderRadius: "50%",
                  width: 25,
                  height: 25,
                  justifyContent: "center",
                  alignItems: "center",
                }}
                display={"flex"}
                bg={
                  joinRes.isConnected ? "var(--clr-primary-100)" : "yellow.0"
                }>
                <IconHourglassEmpty
                  size={15}
                  color={
                    joinRes.isConnected
                      ? "var(--clr-primary-400)"
                      : "var(--mantine-color-yellow-6)"
                  }
                />
              </Box>
              <Text fz={"sm"} fw={500}>
                {Math.floor(countSeconds / 60)
                  .toLocaleString()
                  .padStart(2, "0")}
                :{(countSeconds % 60).toLocaleString().padStart(2, "0")}
              </Text>
            </Group>
          </div>
        </div>

        <Tooltip zIndex={9999} label={t("minimize")} withArrow>
          <ActionIcon
            variant="transparent"
            className="maximize-btn"
            onClick={() => setIsMaximized(a => !a)}>
            <IconArrowsDiagonalMinimize2 size={18} color="white" />
          </ActionIcon>
        </Tooltip>
        <audio ref={userJoinCallAudioRef} src={UserJoinCallSound}>
          <track kind="captions" src="" srcLang="en" label="English" default />
        </audio>

        <div className="user-list">
          <div
            className={`user local ${
              isLocalVoiceDetected && isMicOn ? "voice-detected" : ""
            } ${remoteUsers.length ? "" : "no-remote-user"}`}>
            <LocalUser
              playAudio={false} // Whether or not to play local user's audio (mirroring microphone)
              // playVideo={false} // Whether or not to play local user's video (mirroring camera)
              audioTrack={localMicrophoneTrack}
              cameraOn={isCameraOn}
              micOn={isMicOn}
              videoTrack={localCameraTrack}
              // cover={"https://avatars.githubusercontent.com/u/44934037?v=4"}
              // cover={PlaceholderPerson}>
              cover={""}>
              {!isCameraOn && (
                <Avatar className="avatar" color="blue.6" bg={"blue.1"}>
                  {getInitials(username)}
                </Avatar>
              )}
              <div className="username-wrapper">
                <samp className="username">{t("you")}</samp>
              </div>
            </LocalUser>
            {!isMicOn && (
              <div className="microphone-wrapper">
                <IconMicrophoneOff size={16} color="white" />
              </div>
            )}
            <div className="network-icon-wrapper">{getNetworkDelay(delay)}</div>
          </div>

          {remoteUsers.map(user => (
            <div
              className={`user remote ${
                isRemoteVoiceDetected ? "voice-detected" : ""
              }`}
              key={user.uid}>
              <RemoteUser
                cover={() => (
                  <Avatar className="avatar" color="accent.6" bg={"accent.1"}>
                    {getInitials(meetingToken?.data?.patient_name)}
                  </Avatar>
                )}
                user={user}>
                {!user.audioTrack?.isPlaying ? (
                  <div className="microphone-wrapper">
                    <IconMicrophoneOff size={16} color="white" />
                  </div>
                ) : null}
                <div className="username-wrapper">
                  <samp className="username">
                    {truncateWithEllipsis(meetingToken?.data?.patient_name, 20)}
                  </samp>
                </div>
              </RemoteUser>
            </div>
          ))}
        </div>

        {/* Controls */}
        <StyledVideoCallControls className="is-maximized">
          <Tooltip
            withArrow
            label={isMicOn ? t("turn-mic-on") : t("turn-mic-off")}
            zIndex={9999}>
            <ActionIcon
              size={45}
              radius={"xl"}
              variant="outline"
              onClick={() => {
                setIsMicOn(a => !a);
              }}
              color={"white"}>
              {isMicOn ? (
                <IconMicrophone size={20} />
              ) : (
                <IconMicrophoneOff size={20} />
              )}
            </ActionIcon>
          </Tooltip>
          <Tooltip
            withArrow
            label={isCameraOn ? t("turn-cam-off") : t("turn-cam-on")}
            zIndex={9999}>
            <ActionIcon
              size={45}
              radius={"xl"}
              variant="outline"
              onClick={() => {
                setIsCameraOn(a => !a);
              }}
              color={"white"}>
              {isCameraOn ? (
                <IconVideo size={20} />
              ) : (
                <IconVideoOff size={20} />
              )}
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t("end-call")} zIndex={9999} withArrow>
            <ActionIcon
              className="end-call-btn"
              size={45}
              radius={"xl"}
              variant="filled"
              onClick={() => {
                if (remoteUsers.length) {
                  openLeaveCallWarningModal({
                    countSeconds: countSeconds,
                    patientFirstName:
                      meetingToken?.data?.patient_name?.split(" ")[0],
                    setCallDuration: setCallDuration,
                    setIsCalling: setIsCalling,
                    setVideoCallThreshold: setVideoCallThreshold,
                  });
                } else {
                  setCallDuration(countSeconds);
                  setIsCalling(false);
                }
              }}
              color={"red"}>
              {/* <MdOutlineCallEnd size={20} /> */}
              <IconPhone style={{ rotate: "135deg" }} size={26} />
            </ActionIcon>
          </Tooltip>
        </StyledVideoCallControls>
      </StyledVideoCall>,
      document.body
    );
  } else
    return createPortal(
      <Draggable handle=".drag-btn" bounds="html">
        <StyledVideoCall>
          <audio ref={userJoinCallAudioRef} src={UserJoinCallSound}>
            <track
              kind="captions"
              src=""
              srcLang="en"
              label="English"
              default
            />
          </audio>

          <Tooltip zIndex={9999} withArrow label={t("drag")}>
            <ActionIcon className="drag-btn" variant="transparent">
              <IconGripHorizontal size={20} color="white" />
            </ActionIcon>
          </Tooltip>
          <Tooltip zIndex={9999} withArrow label={t("maximize")}>
            <ActionIcon
              variant="transparent"
              className="maximize-btn"
              onClick={() => setIsMaximized(a => !a)}>
              <IconArrowsDiagonal size={16} color="white" />
            </ActionIcon>
          </Tooltip>

          <div className="user-list">
            <div
              className={`user local ${
                isLocalVoiceDetected && isMicOn ? "voice-detected" : ""
              }`}>
              <LocalUser
                playAudio={false} // Whether or not to play local user's audio (mirroring microphone)
                // playVideo={false} // Whether or not to play local user's video (mirroring camera)
                audioTrack={localMicrophoneTrack}
                cameraOn={isCameraOn}
                micOn={isMicOn}
                videoTrack={localCameraTrack}
                // cover={"https://avatars.githubusercontent.com/u/44934037?v=4"}
                // cover={PlaceholderPerson}>
                cover={""}>
                <Avatar className="avatar" color="blue.6" bg={"blue.1"}>
                  {getInitials(username)}
                </Avatar>

                <div className="username-wrapper">
                  <samp className="username">{t("you")}</samp>
                </div>
              </LocalUser>
            </div>

            {remoteUsers.map(user => (
              <div
                className={`user remote ${
                  isRemoteVoiceDetected ? "voice-detected" : ""
                }`}
                key={user.uid}>
                <RemoteUser
                  cover={() => (
                    <Avatar className="avatar" color="accent.6" bg={"accent.1"}>
                      {getInitials(meetingToken?.data?.patient_name)}
                    </Avatar>
                  )}
                  user={user}>
                  <Group className="network-mic-wrapper" gap={5}>
                    {!user.audioTrack?.isPlaying && (
                      <div>
                        <IconMicrophoneOff size={16} color="white" />
                      </div>
                    )}
                    <div>{getNetworkDelay(delay)}</div>
                  </Group>
                  <div className="username-wrapper">
                    <samp className="username">
                      {truncateWithEllipsis("patient", 20)}
                    </samp>
                  </div>
                </RemoteUser>
              </div>
            ))}
          </div>

          {/* Controls */}
          <StyledVideoCallControls>
            <Tooltip
              zIndex={9999}
              withArrow
              label={isMicOn ? t("turn-mic-off") : t("turn-mic-on")}>
              <ActionIcon
                size={35}
                radius={"xl"}
                variant="outline"
                onClick={() => {
                  setIsMicOn(a => !a);
                }}
                color={"white"}>
                {isMicOn ? (
                  <IconMicrophone size={18} />
                ) : (
                  <IconMicrophoneOff size={18} />
                )}
              </ActionIcon>
            </Tooltip>
            <Tooltip
              zIndex={9999}
              withArrow
              label={isCameraOn ? t("turn-cam-off") : t("turn-cam-on")}>
              <ActionIcon
                size={35}
                radius={"xl"}
                variant="outline"
                onClick={() => {
                  setIsCameraOn(a => !a);
                }}
                color={"white"}>
                {isCameraOn ? (
                  <IconVideo size={18} />
                ) : (
                  <IconVideoOff size={18} />
                )}
              </ActionIcon>
            </Tooltip>
            <Tooltip withArrow label={t("end-call")} zIndex={9999}>
              <ActionIcon
                className="end-call-btn"
                size={35}
                radius={"xl"}
                variant="filled"
                onClick={() => {
                  if (remoteUsers.length) {
                    openLeaveCallWarningModal({
                      countSeconds: countSeconds,
                      patientFirstName:
                        meetingToken?.data?.patient_name?.split(" ")[0],
                      setCallDuration: setCallDuration,
                      setIsCalling: setIsCalling,
                      setVideoCallThreshold: setVideoCallThreshold,
                    });
                  } else {
                    setCallDuration(countSeconds);
                    setIsCalling(false);
                  }
                }}
                color={"red"}>
                {/* <MdOutlineCallEnd size={20} /> */}
                <IconPhone style={{ rotate: "135deg" }} size={20} />
              </ActionIcon>
            </Tooltip>
          </StyledVideoCallControls>
        </StyledVideoCall>
      </Draggable>,
      document.body
    );
};
